// src/utils/platform.js
// Platform detection utilities for device-specific behavior

/**
 * Detect if the user is on macOS (desktop, not iOS)
 */
export const isMacOS = () => {
  if (typeof window === 'undefined') return false;
  
  const platform = (navigator.userAgentData?.platform || navigator.platform || '').toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();
  
  const isMac = platform.startsWith('mac') ||
    userAgent.includes('macintosh') ||
    userAgent.includes('mac os x');
  
  const isIOS = userAgent.includes('iphone') ||
    userAgent.includes('ipad') ||
    userAgent.includes('ipod');

  // iPad in "Request Desktop Website" mode reports a desktop/Mac UA but has touch points.
  // Exclude it so isMacOS() returns false for iPads regardless of UA mode.
  const isLikelyTablet = navigator.maxTouchPoints > 1 &&
    window.screen.width >= 700 &&
    window.screen.width < 1400;
  
  return isMac && !isIOS && !isLikelyTablet;
};

/**
 * Detect if the user is on Safari browser
 */
export const isSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && 
    !ua.includes('chrome') && 
    !ua.includes('chromium') &&
    !ua.includes('edg');
};

/**
 * Detect if the user is on iOS (iPhone, iPad, iPod)
 */
export const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod');
};

/**
 * Detect if the user is on any Apple device (macOS or iOS)
 */
export const isAppleDevice = () => isMacOS() || isIOS();

/**
 * Get device type: 'phone' | 'tablet' | 'desktop'
 *
 * Strategy:
 * 1. Query param override (?deviceType=phone|tablet|desktop) for dev/testing
 * 2. UA-based primary detection
 * 3. maxTouchPoints + screen.width fallback to catch iPad in desktop mode
 *
 * Uses window.screen.width (physical screen, constant) not window.innerWidth
 * (changes on resize). A desktop browser resized small stays 'desktop'.
 */
export const getDeviceType = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop';
  }

  // Dev/test override via query param
  try {
    const params = new URLSearchParams(window.location.search);
    const override = params.get('deviceType');
    if (override === 'phone' || override === 'tablet' || override === 'desktop') {
      return override;
    }
  } catch (_) {}

  const ua = navigator.userAgent;

  // iPhone or iPod → always phone
  if (/iPhone|iPod/.test(ua)) return 'phone';

  // Explicit iPad UA → tablet
  if (/iPad/.test(ua)) return 'tablet';

  // Android: "Mobile" in UA = phone, otherwise tablet
  if (/android/i.test(ua)) {
    return /Mobile/i.test(ua) ? 'phone' : 'tablet';
  }

  // Other explicit mobile UA strings
  if (/webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'phone';

  // iPad in "Request Desktop Website" mode: UA says desktop but physically a tablet.
  // screen.width is the physical screen width — not affected by window resize.
  const isTouch = navigator.maxTouchPoints > 1;
  const screenWidth = window.screen.width;
  if (isTouch && screenWidth >= 700 && screenWidth < 1400) return 'tablet';

  return 'desktop';
};

/**
 * Legacy alias — kept so any non-session code importing isMobileDevice still compiles.
 * @deprecated Use getDeviceType() instead.
 */
export const isMobileDevice = () => {
  const t = getDeviceType();
  return t === 'phone' || t === 'tablet';
};

/**
 * Get browser name for display purposes
 */
export const getBrowserName = () => {
  if (typeof window === 'undefined') return 'Unknown';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('firefox')) return 'Firefox';
  if (ua.includes('edg')) return 'Edge';
  if (ua.includes('chrome')) return 'Chrome';
  if (ua.includes('safari')) return 'Safari';
  if (ua.includes('opera') || ua.includes('opr')) return 'Opera';
  return 'Unknown';
};
